/* =========================================================
   nebulyn-product.js — FULL REWRITE (Dawn-safe + stable)
   Goals:
   - ✅ CTA alignment (Qty + ATC row; Buy Now + COD below)
     - IMPORTANT: never move .product-form__buttons or anything out of <form>
     - IMPORTANT: never touch input[name="id"] / variant selection
   - Continue Browsing CTA (referrer + localStorage)
   - Store last browse URL (collections/search/pages)
   - Fit pills: carry current Size/Color into the "other fit" link
   - Apply incoming ?size= & ?color= SAFELY (click, no checked hacks)
   - ✅ Variant ID fix: if ?variant= exists, NEVER override with size/color clicks
   - Variant media filtering WITHOUT rebuilding the gallery DOM
   - Fit Finder open hook
========================================================= */

(() => {
  'use strict';
  /* -----------------------------
     Helpers
  ----------------------------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
  const normalize = (s) => String(s ?? '').trim().toLowerCase();

  function safeGetLS(key) {
    try { return localStorage.getItem(key) || ''; } catch (_) { return ''; }
  }
  function safeSetLS(key, val) {
    try { localStorage.setItem(key, val); } catch (_) {}
  }

  function isSameOriginUrl(url) {
    try { return new URL(url, window.location.origin).origin === window.location.origin; }
    catch (_) { return false; }
  }

  function isBrowseUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      const p = u.pathname || '';
      return p.startsWith('/collections') || p.startsWith('/search') || p.startsWith('/pages');
    } catch (_) {
      return false;
    }
  }

  function waitFor(checkFn, { timeoutMs = 3000, intervalMs = 50 } = {}) {
    const start = Date.now();
    return new Promise((resolve) => {
      const t = setInterval(() => {
        let ok = false;
        try { ok = !!checkFn(); } catch (_) {}
        if (ok) {
          clearInterval(t);
          resolve(true);
          return;
        }
        if (Date.now() - start > timeoutMs) {
          clearInterval(t);
          resolve(false);
        }
      }, intervalMs);
    });
  }

  function getProductRoot() {
    return qs('product-info.neb-main-product') || qs('product-info') || document;
  }

  function getOptionRadios(optionName) {
    const root = getProductRoot();
    const needle = normalize(optionName);
    return qsa('input[type="radio"]', root).filter((r) => normalize(r.name).includes(needle));
  }

  function getSelectedOptionValueByName(optionName) {
    const root = getProductRoot();
    const needle = normalize(optionName);

    // Radios
    const radios = qsa('input[type="radio"]', root).filter((r) => normalize(r.name).includes(needle));
    const checked = radios.find((r) => r.checked);
    if (checked) return checked.value;

    // Select fallback
    const selects = qsa('select', root);
    const sel = selects.find((s) => normalize(s.name).includes(needle) || normalize(s.id).includes(needle));
    if (sel && sel.value) return sel.value;

    return '';
  }

  function getSelectedSizeColor() {
    const size = getSelectedOptionValueByName('size');
    const color = getSelectedOptionValueByName('color') || getSelectedOptionValueByName('colour');
    return { size, color };
  }

  function setRadioByValueClick(radios, value) {
    const target = normalize(value);
    const match = radios.find((r) => normalize(r.value) === target);
    if (match) {
      match.click(); // ✅ let Dawn handle everything
      return true;
    }
    return false;
  }

  function hideEl(el) { if (el) el.style.display = 'none'; }
  function showEl(el) { if (el) el.style.display = 'block'; }

  /* =========================================================
     1) Store last browse URL
========================================================= */
  function initBrowseUrlTracker() {
    try {
      const path = window.location.pathname || '';
      if (path.startsWith('/collections') || path.startsWith('/search') || path.startsWith('/pages')) {
        safeSetLS('neb_last_browse_url', window.location.href);
      }
    } catch (_) {}
  }

  /* =========================================================
     2) Continue browsing CTA (reliable)
========================================================= */
  function initContinueBrowsingCTA() {
    const cta = qs('.neb-continue-browsing');
    if (!cta) return;

    const HOME = '/';
    const FALLBACK = '/collections/all';

    const isSamePage = (a, b) => {
      try {
        const ua = new URL(a, window.location.origin);
        const ub = new URL(b, window.location.origin);
        return (ua.pathname + ua.search) === (ub.pathname + ub.search);
      } catch (_) {
        return false;
      }
    };

    const isValidBrowseCandidate = (url) => {
      if (!url) return false;
      if (!isSameOriginUrl(url)) return false;
      if (!isBrowseUrl(url)) return false;
      if (isSamePage(url, window.location.href)) return false;
      return true;
    };

    const pickBestUrl = () => {
      const ref = (document.referrer || '').trim();
      if (isValidBrowseCandidate(ref)) return ref;

      const stored = (safeGetLS('neb_last_browse_url') || '').trim();
      if (isValidBrowseCandidate(stored)) return stored;

      return HOME || FALLBACK;
    };

    cta.href = pickBestUrl();

    cta.addEventListener('click', () => {
      cta.href = pickBestUrl();
    });

    setTimeout(() => { cta.href = pickBestUrl(); }, 0);
  }

  /* =========================================================
     3) Fit pills + option param syncing
     ✅ Variant ID fix:
       - If ?variant= exists → DO NOT apply ?size/?color
       - Still update Fit-pill links with current selection
========================================================= */
  function initFitPillParamSync() {
    const root = getProductRoot();
    const fitPills = qs('.neb-fit-pills', root);
    if (!fitPills) return;

    const fitLinks = qsa('a.neb-pill[href]', fitPills);
    if (!fitLinks.length) return;

    const warningSize = qs('#neb-fit-warning-size', root);
    const warningColor = qs('#neb-fit-warning-color', root);
    const hideWarnings = () => { hideEl(warningSize); hideEl(warningColor); };

    function updateFitLinksFromSelection() {
      const { size, color } = getSelectedSizeColor();

      fitLinks.forEach((a) => {
        const rawHref = a.getAttribute('href') || '';
        if (!rawHref) return;

        let url;
        try { url = new URL(rawHref, window.location.origin); }
        catch (_) { return; }

        const params = new URLSearchParams();
        if (size) params.set('size', size);
        if (color) params.set('color', color);

        const q = params.toString();
        url.search = q ? `?${q}` : '';
        a.href = url.toString();
      });
    }

    async function applyIncomingParamsSafely() {
      hideWarnings();

      const sp = new URLSearchParams(window.location.search);
      const urlVariant = sp.get('variant') || '';
      const urlSize = sp.get('size') || '';
      const urlColor = sp.get('color') || '';

      const syncOnly = () => raf2(updateFitLinksFromSelection);

      // ✅ If variant is present, respect it — do NOT force size/color
      if (urlVariant) {
        await waitFor(() => {
          const idInput = qs('input[name="id"]', root);
          return idInput && String(idInput.value || '') === String(urlVariant);
        }, { timeoutMs: 3500 });

        syncOnly();
        return;
      }

      // No params → do nothing except sync links
      if (!urlSize && !urlColor) {
        syncOnly();
        return;
      }

      // Wait for Dawn to be ready
      const ok = await waitFor(() => qs('input[name="id"]', root), { timeoutMs: 3500 });
      if (!ok) {
        syncOnly();
        return;
      }

      let sizeMatched = true;
      let colorMatched = true;

      if (urlSize) {
        const sizeRadios = getOptionRadios('size');
        if (sizeRadios.length) sizeMatched = setRadioByValueClick(sizeRadios, urlSize);
      }

      if (urlColor) {
        const colorRadios = getOptionRadios('color').concat(getOptionRadios('colour'));
        if (colorRadios.length) colorMatched = setRadioByValueClick(colorRadios, urlColor);
      }

      if (urlSize && !sizeMatched) showEl(warningSize);
      if (urlColor && !colorMatched) showEl(warningColor);

      syncOnly();
    }

    // Update links on option changes
    root.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;

      if (
        t.matches('input[type="radio"], select') &&
        (
          normalize(t.name).includes('size') ||
          normalize(t.name).includes('color') ||
          normalize(t.name).includes('colour') ||
          normalize(t.id).includes('size') ||
          normalize(t.id).includes('color') ||
          normalize(t.id).includes('colour')
        )
      ) {
        hideWarnings();
        updateFitLinksFromSelection();
      }
    });

    // Run param application once after load + once after first variant event
    document.addEventListener('variant:change', () => applyIncomingParamsSafely(), { once: true });
    setTimeout(() => applyIncomingParamsSafely(), 400);

    updateFitLinksFromSelection();
  }

  /* =========================================================
     4) Variant media filter (NON-DESTRUCTIVE)
     - Does NOT rebuild DOM
     - Only toggles visibility of existing slides/thumbs
========================================================= */
  function initVariantMediaFilter() {
    const root = qs('[data-neb-product-handle]');
    if (!root) return;

    const handle = root.getAttribute('data-neb-product-handle');
    if (!handle) return;

    const slideList =
      qs('media-gallery ul') ||
      qs('[id^="Slider-Gallery"]') ||
      qs('.product__media-list');

    const thumbList =
      qs('[id^="Slider-Thumbnails"]') ||
      qs('.thumbnail-list');

    if (!slideList) return;

    const slides = Array.from(slideList.children);
    const thumbs = thumbList ? Array.from(thumbList.children) : [];

    function getSelectedVariantId() {
      const idInput = qs('input[name="id"]');
      const v = idInput ? parseInt(idInput.value, 10) : NaN;
      return Number.isFinite(v) ? String(v) : null;
    }

    function nodeMediaId(node) {
      const d = node.getAttribute('data-media-id');
      if (d) return String(d).split('-').pop();
      const inner = node.querySelector('[data-media-id]');
      if (inner) return String(inner.getAttribute('data-media-id')).split('-').pop();
      return null;
    }

    let mediaVariantMap = null;

    async function fetchProductMediaMap() {
      try {
        const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
        if (!r.ok) return null;
        const productData = await r.json();
        if (!productData || !Array.isArray(productData.media)) return null;

        const m = new Map();
        productData.media.forEach((x) => {
          m.set(String(x.id), Array.isArray(x.variant_ids) ? x.variant_ids.map(String) : []);
        });
        return m;
      } catch (_) {
        return null;
      }
    }

    function setNodeVisible(node, visible) {
      node.hidden = !visible;
      node.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) node.style.removeProperty('display');
      else node.style.display = 'none';
    }

    function applyVisibility(list, variantId) {
      if (!mediaVariantMap || !variantId) return;

      const allowed = new Set();
      mediaVariantMap.forEach((variantIds, mediaId) => {
        if (variantIds.includes(String(variantId))) allowed.add(String(mediaId));
      });

      const hasScoped = allowed.size > 0;

      list.forEach((node) => {
        const mid = nodeMediaId(node);
        if (!mid) return setNodeVisible(node, true);
        setNodeVisible(node, hasScoped ? allowed.has(String(mid)) : true);
      });
    }

    function applyFilter() {
      const variantId = getSelectedVariantId();
      if (!variantId) return;

      applyVisibility(slides, variantId);
      if (thumbs.length) applyVisibility(thumbs, variantId);

      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }

    (async () => {
      mediaVariantMap = await fetchProductMediaMap();
      if (!mediaVariantMap) return;

      applyFilter();

      document.addEventListener('change', (e) => {
        if (e.target.closest('variant-selects, variant-radios, .product-form__input, .product-form')) {
          setTimeout(applyFilter, 0);
        }
      });

      document.addEventListener('variant:change', () => setTimeout(applyFilter, 0));
      document.addEventListener('shopify:variant:change', () => setTimeout(applyFilter, 0));
    })();
  }

  /* =========================================================
     5) Fit Finder open (toolbar / button)
========================================================= */
  function initFitFinderOpen() {
    document.addEventListener('click', (e) => {
      const fitBtn = e.target.closest('[data-neb-open-fit]');
      if (!fitBtn) return;

      const existingTrigger = qs('.neb-fit-trigger');
      if (existingTrigger) {
        existingTrigger.click();
        return;
      }

      const overlay = qs('.neb-fit-overlay');
      const modal = qs('.neb-fit-modal');
      if (overlay && modal) {
        overlay.classList.add('is-open');
        modal.classList.add('is-open');
        document.documentElement.classList.add('neb-no-scroll', 'neb-fit-open');
      }
    });
  }

  /* =========================================================
     Boot
========================================================= */
  function boot() {
    initBrowseUrlTracker();
    initContinueBrowsingCTA();
    initFitPillParamSync();
    initVariantMediaFilter();
    initFitFinderOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();